import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { TableLoader } from 'components/TableLoader';
import { findAllConstructionTypesFromObjects } from 'components/WorkOrderConstructionTypesTabSelect';
import { RequestStatus } from 'features/request/requestSlice';
import type { WorkOrderChangeObjectItem } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { CreateOther } from 'pages/Project/Common/Contract/OtherCreate';
import { useConstructionTypesListItems } from 'pages/Project/Common/hooks/useConstructionTypesListItems';
import Styles from 'pages/Project/UerProject/WorkOrder/AddOrderOther/AddOrderOther.module.css';
import { getAdditionalClass } from 'pages/Project/UerProject/WorkOrder/AddOrderOther/AddOthersTable';
import { IconAlert, Informer, Table } from 'uikit';

import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
    useAddItemsToWorkOrderChange,
    useAddOtherToWorkOrderChangeFilterConfig,
    useAddOtherToWorkOrderChangeTableData,
} from '../hooks';
import { addOthersToWorkOrderChange } from '../WorkOrderChangeQueries';

export const AddOtherToWorkOrderChangeTable = ({
    constructionTypeId,
    handlerSuccess,
    handlerClose,
    workOrderChangeObjectsData,
    workOrderChangeUerRules,
}: {
    constructionTypeId: number;
    handlerSuccess: () => void;
    handlerClose: () => void;
    workOrderChangeObjectsData: WorkOrderChangeObjectItem[];
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
}) => {
    const { id: projectId, orderChangeId } = useParams();
    const { getConstructionTypeName } = useConstructionTypesListItems();

    const { projectData } = useProjectData(projectId);
    const workOrderChangeId = useMemo(() => Number(orderChangeId), [orderChangeId]);

    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);

    const handleCloseExactly = useCallback(() => {
        handlerClose();
    }, [handlerClose]);

    const handleCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const {
        tableRows,
        tableColumns,
        variablesForAdd,
        getTableRowsStatus,
        btnConfirmDisabled,
        haveCheckedOthers,
        handleFilterChange,
        reloadTableAfterCreate,
    } = useAddOtherToWorkOrderChangeTableData({
        workOrderChangeId,
        constructionTypeId,
        workOrderChangeObjectsData,
        costRounding: projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0,
    });

    const handleCancel = useCallback(() => {
        if (haveCheckedOthers) {
            setIsOpenWarningDialog(true);
        } else {
            handlerClose();
        }
    }, [handlerClose, haveCheckedOthers]);

    const {
        addToWorkOrderChangeHandler,
        addResponse,
        addStatus,
        addErr,
        addIsSubmitted: isSubmitted,
        closeErrorModal,
        handleCreateReport,
    } = useAddItemsToWorkOrderChange({
        query: addOthersToWorkOrderChange,
        requestId: 'uerAddWorkOrderChangeOtherCost',
        variables: variablesForAdd,
        onSuccessAddHandler: handlerSuccess,
    });

    const tableFilters = useAddOtherToWorkOrderChangeFilterConfig({
        workOrderChangeId,
        constructionTypeId,
    });

    const constructionTypes = useMemo(
        () => findAllConstructionTypesFromObjects({ workOrderChangeObjectsData }),
        [workOrderChangeObjectsData],
    );

    return (
        <ModalDialog
            title={`Лимиты прочих затрат для вида строительства "${getConstructionTypeName(constructionTypeId)}"`}
            isOpen
            onConfirm={addToWorkOrderChangeHandler}
            onClose={handleCancel}
            btnConfirmLabel="Добавить в Изм. к НЗ"
            withTitleCloseButton
            leftPanel={
                <CreateOther
                    currentConstructionTypeId={constructionTypeId}
                    reloadHandler={reloadTableAfterCreate}
                    constructionTypes={constructionTypes}
                    methodology={ProjectMethodologyEnum.Uer}
                    accessRules={workOrderChangeUerRules}
                />
            }
            style={{ maxWidth: 1340 }}
            isCommit={addStatus === RequestStatus.FETCHING}
            btnConfirmDisabled={btnConfirmDisabled || !workOrderChangeUerRules.canAddingEntries}
            isOnClickOutsideDisable
        >
            <div style={{ margin: '20px' }}>
                <Informer
                    status="system"
                    label="Выбранные прочие затраты по умолчанию будут применены ко всем объектам данного вида строительства. Для того, чтобы исключить объекты, нажмите кнопку раскрытия и снимите выделение."
                    icon={IconAlert}
                />
            </div>
            <Table
                columns={tableColumns}
                rows={tableRows}
                size="s"
                className={`${Styles.otherTable} workOrderPrice`}
                stickyHeader
                emptyRowsPlaceholder={<TableLoader requestStatus={getTableRowsStatus} />}
                getAdditionalClassName={getAdditionalClass}
                verticalAlign="center"
                filters={tableFilters}
                onFiltersUpdated={handleFilterChange}
            />
            <ErrorHandlingModal
                titleText="Добавить прочие"
                isSubmitted={isSubmitted}
                statusRequest={addStatus}
                dataRequest={addResponse?.uerAddWorkOrderChangeOtherCost}
                errorsRequest={addErr}
                handleCloseModalError={closeErrorModal}
                handleCreateReport={handleCreateReport}
            />
            {isOpenWarningDialog && (
                <ConfirmationDialog
                    textHeader=""
                    textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                    cancelButtonText="Не отменять"
                    confirmButtonText="Отменить"
                    isOpened
                    onSubmit={handleCloseExactly}
                    onClose={handleCancelWarningDialog}
                />
            )}
        </ModalDialog>
    );
};

AddOtherToWorkOrderChangeTable.displayName = 'AddOtherToWorkOrderChangeTable';
